import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toast, ToastItem } from '@/stores/slices/notification/toastSlice';
import { SERVICE_TYPE } from '@/api/type/commonTypes';
import { Images } from '@/assets';
import { formatPartnerAlarmMsg } from '@/utils/alarm-message/formatPartnerAlarmMessage';
import { formatTime } from '@/utils/date-time/formatTime';
import { langPath } from '@/utils/langPath';

const AUTO_DISMISS_MS = 40000;

interface Props {
  item: ToastItem;
}

const alarmIcon = (serviceType: SERVICE_TYPE) => {
  switch (serviceType) {
    case 'CROWN_BRIDGE':
      return Images.OrderCrownBridgeImage;
    case 'FACE_GIDE':
      return Images.OrderFaceGideImage;
    case 'MEG_ALIGNER':
      return Images.OrderMegAlignerImage;
    case 'SURGICAL_GUIDE':
      return Images.OrderR2GuideImage;
    case 'R2_QVD':
      return Images.OrderQvdImg;
    default:
      return Images.ErrorDeliverableImg;
  }
};

export const OrderToast = ({ item }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(toast.removeToast(item.toastId));
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [dispatch, item.toastId]);

  const handleClick = useCallback(() => {
    dispatch(toast.removeToast(item.toastId));
    navigate(langPath('/order/orderList'));
  }, [dispatch, item.toastId, navigate]);

  const playerName = `${item.actorName.lastName}${item.actorName.firstName}`;
  const statusText = formatPartnerAlarmMsg(item.alarmType, t);

  return (
    <ToastCard onClick={handleClick}>
      <Icon src={alarmIcon(item.serviceType)} alt={item.serviceType} />
      <Content>
        <MainText>
          [{item.orderId}] {playerName}
        </MainText>
        <StatusText>{statusText}</StatusText>
      </Content>
      <Time>{formatTime(item.createdAt)}</Time>
    </ToastCard>
  );
};

const ToastCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 320px;
  padding: 12px 16px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  animation: slide-in 0.25s ease-out;
  box-sizing: border-box;
  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.16);
  }

  @keyframes slide-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Icon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

const Content = styled.div`
  min-width: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  flex: 1;
`;

const MainText = styled.div`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusText = styled.div`
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Time = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: #9ca3af;
`;
